/**
 * Autonomous Strategy Queue
 * ---------------------------------------------------------------
 * Tactical command panel inside Admin → Viral.
 *
 * Before generation, the orchestrator proposes WHAT to generate
 * next based on telemetry + drift + saturation + memory. The
 * operator can:
 *   - accept the strategy in full
 *   - partially accept individual directives
 *   - reject
 *   - lock specific archetypes / formats / escalations so the
 *     orchestrator can never adjust them
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Loader2, Brain, Lock, Unlock, Check, X, RefreshCw, Crosshair, Sparkles, AlertTriangle,
} from "lucide-react";
import {
  proposeStrategy, commitStrategy, type StrategyCycle, type StrategyAction,
  STRATEGY_PROPOSED_EVENT, STRATEGY_ACCEPTED_EVENT,
} from "@/lib/strategyOrchestrator";

const titleCase = (s: string | null | undefined) =>
  (s ?? "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "—";

const LOCKS_KEY = "greyrock:strategy:locks";
const loadLocks = (): Record<string, string[]> => {
  try { return JSON.parse(localStorage.getItem(LOCKS_KEY) ?? "{}"); } catch { return {}; }
};
const saveLocks = (l: Record<string, string[]>) => localStorage.setItem(LOCKS_KEY, JSON.stringify(l));

const TICKERS = [
  "Scanning live telemetry…",
  "Reconciling synthetic vs reality…",
  "Mapping behavioral opportunity zones…",
  "Detecting saturation pressure…",
  "Calibrating tactical directives…",
];

const AutonomousStrategyQueue = () => {
  const [proposal, setProposal] = useState<StrategyCycle | null>(null);
  const [generating, setGenerating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  const [locks, setLocks] = useState<Record<string, string[]>>(() => loadLocks());
  const [tickerIdx, setTickerIdx] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setTickerIdx((i) => (i + 1) % TICKERS.length), 2800);
    return () => window.clearInterval(id);
  }, []);

  const propose = useCallback(async () => {
    setGenerating(true);
    try {
      const next = await proposeStrategy(locks);
      setProposal(next);
      setAccepted(new Set(next.actions.map((a) => `${a.dimension}::${a.target}`)));
      window.dispatchEvent(new CustomEvent(STRATEGY_PROPOSED_EVENT, { detail: { cycleNumber: next.cycle_number } }));
    } finally {
      setGenerating(false);
    }
  }, [locks]);

  useEffect(() => { void propose(); /* eslint-disable-next-line */ }, []);

  const toggleAction = (key: string) => {
    setAccepted((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleLock = (dimension: string, target: string) => {
    setLocks((prev) => {
      const cur = new Set(prev[dimension] ?? []);
      cur.has(target) ? cur.delete(target) : cur.add(target);
      const next = { ...prev, [dimension]: Array.from(cur) };
      saveLocks(next);
      return next;
    });
  };

  const isLocked = (dimension: string, target: string) =>
    (locks[dimension] ?? []).includes(target);

  const acceptedActions = useMemo(() => {
    if (!proposal) return [] as StrategyAction[];
    return proposal.actions.filter((a) => accepted.has(`${a.dimension}::${a.target}`));
  }, [proposal, accepted]);

  const commit = async (status: "accepted" | "partial" | "rejected") => {
    if (!proposal) return;
    setCommitting(true);
    try {
      await commitStrategy(proposal, status, status === "rejected" ? [] : acceptedActions);
      // Refresh proposal afterwards so user sees a new cycle
      await propose();
    } finally {
      setCommitting(false);
    }
  };

  const recommendation = proposal
    ? `${titleCase(proposal.dominant_format)} + ${titleCase(proposal.dominant_archetype)}${proposal.dominant_escalation ? ` + ${titleCase(proposal.dominant_escalation)} Escalation` : ""}`
    : "—";

  return (
    <Card className="bg-gradient-to-br from-charcoal-deep to-charcoal-medium border-cyan/25">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Brain className="w-4 h-4 text-cyan animate-pulse" />
          Autonomous Strategy Queue
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-1">
            Pre-generation behavioral command · operator override available
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Live ticker */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-cyan opacity-70 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan" />
            </span>
            <span className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground truncate">
              {generating ? "Orchestrator computing…" : TICKERS[tickerIdx]}
            </span>
          </div>
          <Button
            variant="outline" size="sm"
            onClick={() => void propose()}
            disabled={generating || committing}
            className="border-cyan/40 text-cyan hover:bg-cyan/10 font-mono text-[10px] uppercase tracking-wider"
          >
            {generating ? (<><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Computing</>) : (<><RefreshCw className="w-3 h-3 mr-1.5" />Recompute</>)}
          </Button>
        </div>

        {generating && !proposal ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-cyan" /></div>
        ) : !proposal ? (
          <div className="text-[11px] font-mono text-muted-foreground text-center py-8">
            No proposal yet · click recompute
          </div>
        ) : (
          <>
            {/* Recommendation header */}
            <div className="rounded-lg border border-cyan/30 bg-charcoal-deep/70 p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-cyan/80 font-mono">
                  Recommended · Cycle #{proposal.cycle_number}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="border-cyan/40 text-cyan font-mono text-[10px]">
                    Confidence {proposal.confidence}%
                  </Badge>
                  {proposal.saturation_risk > 60 && (
                    <Badge variant="outline" className="border-amber-400/40 text-amber-300 font-mono text-[10px]">
                      <AlertTriangle className="w-3 h-3 mr-1" />Saturation {proposal.saturation_risk}%
                    </Badge>
                  )}
                </div>
              </div>
              <div className="text-lg font-bold text-foreground mt-2">{recommendation}</div>
              <div className="text-xs text-muted-foreground italic mt-1">
                {proposal.rationale} · {proposal.dominant_platform ? `target ${titleCase(proposal.dominant_platform)}` : "cross-platform"}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-glass-border">
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-mono">Projected Virality</div>
                  <div className="text-xl font-mono text-cyan">{proposal.projected_virality}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-mono">Projected Replay</div>
                  <div className="text-xl font-mono text-amber-300">{proposal.projected_replay_rate}%</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-mono">Directives</div>
                  <div className="text-xl font-mono text-foreground">{proposal.actions.length}</div>
                </div>
              </div>
            </div>

            {/* Directive cards (manual override) */}
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80 font-mono flex items-center gap-1">
                <Crosshair className="w-3 h-3 text-cyan" />Tactical Directives · toggle or lock
              </div>
              <ScrollArea className="max-h-[320px]">
                <div className="space-y-1.5">
                  {proposal.actions.length === 0 ? (
                    <div className="text-[11px] font-mono text-muted-foreground text-center py-4">
                      No directives generated · system stable
                    </div>
                  ) : proposal.actions.map((a) => {
                    const key = `${a.dimension}::${a.target}`;
                    const isAccepted = accepted.has(key);
                    const locked = isLocked(a.dimension, a.target);
                    const positive = a.adjustment >= 0;
                    return (
                      <div
                        key={key}
                        className={`flex items-start gap-3 p-3 rounded-md border ${isAccepted ? "border-cyan/30 bg-cyan/5" : "border-glass-border bg-charcoal-deep/30"}`}
                      >
                        <Checkbox
                          checked={isAccepted}
                          onCheckedChange={() => toggleAction(key)}
                          disabled={locked}
                          className="mt-0.5 border-cyan/40 data-[state=checked]:bg-cyan data-[state=checked]:text-charcoal-deep"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-[9px] uppercase font-mono border-cyan/30 text-cyan/90">
                              {a.action_type}
                            </Badge>
                            <span className="text-xs font-mono text-foreground">{titleCase(a.target)}</span>
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">{a.dimension}</span>
                            <span className={`text-[10px] font-mono ml-auto ${positive ? "text-emerald-300" : "text-amber-300"}`}>
                              {positive ? "+" : ""}{Math.round(a.adjustment * 100)}% · {Math.round(a.confidence)}%
                            </span>
                          </div>
                          <div className="text-[11px] text-muted-foreground italic mt-1">{a.reasoning}</div>
                        </div>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => toggleLock(a.dimension, a.target)}
                          className={`h-7 px-2 text-[10px] uppercase tracking-wider font-mono ${locked ? "text-amber-300 hover:text-amber-200" : "text-muted-foreground hover:text-foreground"}`}
                          title={locked ? "Unlock" : "Lock so orchestrator cannot touch"}
                        >
                          {locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>

            {/* Active locks summary */}
            {Object.values(locks).some((arr) => arr.length > 0) && (
              <div className="text-[10px] font-mono text-amber-200/80 flex flex-wrap gap-1 items-center">
                <Lock className="w-3 h-3" />
                <span className="uppercase tracking-wider mr-1">Locked:</span>
                {Object.entries(locks).flatMap(([dim, arr]) =>
                  arr.map((t) => (
                    <Badge key={`${dim}:${t}`} variant="outline" className="text-[9px] border-amber-400/30 text-amber-200 font-mono">
                      {dim}·{titleCase(t)}
                    </Badge>
                  ))
                )}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-glass-border">
              <Button
                onClick={() => void commit(accepted.size === proposal.actions.length ? "accepted" : "partial")}
                disabled={committing || accepted.size === 0}
                className="bg-cyan text-charcoal-deep hover:bg-cyan/90 font-mono text-[11px] uppercase tracking-wider"
              >
                {committing ? (<><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Committing</>) :
                  accepted.size === proposal.actions.length ? (<><Sparkles className="w-3 h-3 mr-1.5" />Accept Strategy</>) :
                  (<><Check className="w-3 h-3 mr-1.5" />Accept {accepted.size} of {proposal.actions.length}</>)}
              </Button>
              <Button
                variant="outline"
                onClick={() => void commit("rejected")}
                disabled={committing}
                className="border-glass-border text-muted-foreground hover:text-foreground font-mono text-[11px] uppercase tracking-wider"
              >
                <X className="w-3 h-3 mr-1.5" />Reject Cycle
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AutonomousStrategyQueue;
